'use client';
import React, { createContext, Suspense, useContext, useEffect, useMemo, useState } from "react";

// Key for localStorage
const STORAGE_KEY = "gpu-benchmark-result";

const ONE_HOUR = 60 * 60 * 1000;

export enum GPUPerfLabel {
	Pending = -2,
	None = -1,
	Potato = 0,
	Low = 45,
	Medium = 59,
	Ultra = 65,
}

export type ValidGPUPerfThreshold = Exclude<GPUPerfLabel, GPUPerfLabel.Pending | GPUPerfLabel.None>;

const thresholdsSorted = Object.values(GPUPerfLabel).filter((v): v is GPUPerfLabel => typeof v === "number").sort((a, b) => (a as number) - (b as number)) as number[];

function getThresholdForFPS(fps: number) {
	return thresholdsSorted.findLast(t => fps >= t) ?? GPUPerfLabel.Potato;
}

const GpuPerfContext = createContext({
	status: GPUPerfLabel.Pending,
});

function runWebGLBenchmark(durationMs = 1500): Promise<number> {
	// eslint-disable-next-line no-async-promise-executor
	return new Promise(async (resolve) => {

		// wait for all of the page's content to load
		await new Promise<void>(r => document.readyState === 'complete' ? r() : window.addEventListener("load", ()=>r(), { once: true })); // Wait for full page load

		console.log("Loading GPU benchmark...");

		const canvas = document.createElement("canvas");
		const gl = canvas.getContext("webgl");

		if (!gl) {
			resolve(GPUPerfLabel.None);
			return;
		}

		const vertSrc = `
			attribute vec2 pos;
			void main() {
				gl_Position = vec4(pos, 0.0, 1.0);
			}
		`;
		const fragSrc = `
			void main() {
				gl_FragColor = vec4(1.0, 0.5, 0.0, 1.0);
			}
		`;

		const compileShader = (src: string, type: GLenum) => {
			const shader = gl.createShader(type);
			if (!shader) throw new Error("Failed to create shader");
			gl.shaderSource(shader, src);
			gl.compileShader(shader);
			return shader;
		};

		const vert = compileShader(vertSrc, gl.VERTEX_SHADER);
		const frag = compileShader(fragSrc, gl.FRAGMENT_SHADER);

		const prog = gl.createProgram();
		gl.attachShader(prog, vert);
		gl.attachShader(prog, frag);
		gl.linkProgram(prog);
		gl.useProgram(prog);

		const buffer = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
		gl.bufferData(
			gl.ARRAY_BUFFER,
			new Float32Array([-1, -1, 1, -1, 0, 1]),
			gl.STATIC_DRAW
		);

		const posLoc = gl.getAttribLocation(prog, "pos");
		gl.enableVertexAttribArray(posLoc);
		gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

		console.log("Delaying before running GPU benchmark...");

		// Give the main thread a moment to breathe
		await new Promise(r => setTimeout(r, 1000));

		// wait for 60 frames, also to give the main thread a moment to breathe
		let framesWaited = 0;
		await new Promise<void>(r => {
			const step = () => {
				framesWaited++;
				if (framesWaited >= 120) r();
				else requestAnimationFrame(step);
			};
			requestAnimationFrame(step);
		});

		console.log("Running GPU benchmark...");

		let frames = 0;
		const start = performance.now();

		const loop = () => {
			frames++;
			gl.drawArrays(gl.TRIANGLES, 0, 3);
			const now = performance.now();
			const elapsed = now - start;
			if (elapsed < durationMs) {
				requestAnimationFrame(loop);
			} else {
				const fps = (frames / elapsed) * 1000;
				console.log("GPU Performance fps:", { frames, elapsed, durationMs, fps });
				resolve(fps);
			}
		};

		loop();
	});
}

export function GpuPerformanceProvider({ children }: {readonly children: React.ReactNode }) {
	const [status, setStatus] = useState(GPUPerfLabel.Pending);

	useEffect(() => {
		const cached = localStorage.getItem(STORAGE_KEY);
		if (cached) {
			try {
				const { fps, timestamp } = JSON.parse(cached);
				if (Date.now() - timestamp < ONE_HOUR) {
					setStatus(getThresholdForFPS(fps));
					return;
				}
			} catch {
				// ignore parse errors
			}
		}

		runWebGLBenchmark().then((fps) => {
			const newStatus = getThresholdForFPS(fps);
			setStatus(newStatus);

			localStorage.setItem(
				STORAGE_KEY,
				JSON.stringify({ fps, timestamp: Date.now() })
			);
		});
	}, []);

	const value = useMemo(() => ({ status }), [status]);

	console.log("GPU Performance Status:", status);

	return <GpuPerfContext.Provider value={value}>
		{children}
	</GpuPerfContext.Provider>;
}

export function useGpuPerf() {
	return useContext(GpuPerfContext);
}

export function GPUPerformanceGate({ children, fallback = null, threshold }: { readonly children: React.ReactNode; readonly fallback?: React.ReactNode; readonly threshold: ValidGPUPerfThreshold }): React.ReactNode {
	const { status } = useGpuPerf();

	if (status < threshold) return fallback;
	return <Suspense fallback={fallback}>{children}</Suspense>;
}
